import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CloseIcon from "@mui/icons-material/Close";
import DashboardHeader from "@components/Header/DashboardHeader";
import GenericBreadCrums from "@components/Breadcrumbs";
import { resetSiteRoadWaysState } from "@modules/stepper/redux/siteRoadwaysSlice";
import { resetAmenityState } from "@modules/stepper/redux/amenitesSlice";
import { setActiveStep } from "@modules/stepper/redux/stepperSlice";
import { resetSiteRoadwaysROW } from "@modules/stepper-row/redux/siteRoadwaysROWSlice";
import { resetAmenityROWState } from "@modules/stepper-row/redux/amenitesROWSlice";
import type { AppDispatch, RootState } from "@store/store";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { resetApplicationDashboardState } from "@modules/application-section/application-dashboard/redux/slices/dashboardSlice";
import DisclaimerModal from "@modules/stepper/components/modal/DisclaimerModal";
import { setApplicationType } from "./src/redux/slices/dashboardSlice";
import DisclaimerROWModal from "@modules/stepper/components/modal/DisclaimerROWModal";
import row_Icon from "@assets/svg/row_Icon.svg";
import access_Icon from "@assets/svg/access_Icon.svg";
import { setCoordinateSite } from "@modules/stepper-row/redux/stepperROWSlice";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { resetApplicationSubmitted } from "@modules/application-section/application-details/redux/applicationSubmittedSlice";
import { toast } from "react-toastify";
import AlertDialogBox from "@modules/stepper/components/AlertDialogBox";

const MainDashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { profile, loading, error } = useSelector(
    (state: RootState) => state.profile,
  );

  const [applicationTypes, setApplicationTypes] = useState([
    {
      id: 1,
      title: "Access Permission",
      description: "Permission for construction of Access.",
      iconUrl: (
        <img src={access_Icon} alt="Access" className="dashboard-icon" />
      ),
      selected: false,
    },
    {
      id: 2,
      title: "Right of Way Permission",
      description:
        "Permission for accommodation of Public & Industrial Utilities",
      iconUrl: <img src={row_Icon} alt="Row" className="dashboard-icon" />,
      selected: false,
    },
  ]);

  const [openModal, setOpenModal] = useState(false);
  const [openROWModal, setOpenROWModal] = useState(false);

  // New: docs/frames dialogs
  const [openRequiredDrawingModal, setOpenRequiredDrawingModal] =
    useState(false); // Access
  const [openFrameModal, setOpenFrameModal] = useState(false); // ROW
  const [openGSTAlert, setOpenGSTAlert] = useState(false); // gst alert

  const accessRequired = {
    title: "Required Drawing and Document for Access Permission",
    note: "Applicants are advised to keep all the required documents and drawings ready and accessible prior to initiating the application submission process.",
    drawings: [
      "Certified copy of location plan of the Fuel Station along the National Highway showing details of Right of Way (ROW) of National Highway, existing intersections and the intersecting roads, including existing public roads and other developments, falling within a reach of 1.1 km in each side of the Fuel Station and carriageway",
      "Certified copy of plan of the proposed Fuel Station showing details of deceleration, acceleration lanes, service road (if provided), buffer strip, fuel pump, office, kiosk, lubritorium, air and water supply, toilet & drinking water facilities, drainage details, signages and markings conforming to applicable figures enclosed with these norms",
      " Certified copy of sectional and layout plan showing the elevation of the Fuel Station with respect to the National Highway, proposed slopes for adequate drainage and prevention of water logging, along with detailed chainage, location, public facility arrangements of access (including service road, deceleration/acceleration lanes), and complete geometrics for safe traffic movement. (required)",
      "Drainage plan for the Fuel Station/ Wayside Amenities.",
      "Detailed drawing for agumented/ widening of existing cross drainage structure/ culverts as per site requirement",
      "Certified copy of drawing showing requirement as per norms i.e. Location, Layout Road, Road Marking, Drainage Plan & Crust Composition of access road, etc. (Only If you select Private Property)",
      "The location drawing shall show the relevant details of the reach for 1.1 km along the National Highway on either side of the property. (Only If you select Private Property)",
      "Drainage plan for the Private Properties/ Other Establishments (Other than fuel stations). (Only If you select Private Property)",
      "Detailed drawing for agumented/ widening of existing cross drainage structure/ culverts as per site requirement. (Only If you select Private Property)",
    ],
    documents: [
      "Detail of the material for pavement composition for deceleration lane, service road and acceleration lane.",
      "Detailed survey map from State Revenue Department showing the location of proposed plot and existing NH/ Certifcate that the proposed plot location directly abutts the NH .",
      "Letter of Intent issued by Oil Company",
      "Land Ownership, Lease Agreement, Khata certificate/Record of rights",
      "General Power of Attorney given by Oil Company for signing the License Deed",
      "If Letter of intent (LOI) holder is not landowner, then give lease agreement from landowner to LOI holder for leasing of land on which Retail fuel station is located",

      "Documentary evidence of additional Land Acquisition details (if required) beyond the available ROW (to accommodate service roads, deceleration / acceleration lanes, turning radius etc.) done by the owner of the fuel station.",
      "Fuel station is a Company Owned and Company Operated (COCO) fuel station, please upload lease agreement given by Landowner to Oil Company",
      "Submit reach type document to verify the stretch reach.",
      "Proposed Site Pictures",
      "Any Document to indicate commercial activities are allowed on the land.",
      "Interception Drain Certificate/ Rain Water Drain Design",
    ],
  };

  const rowFrame = {
    title: "Required Drawing and Document for Right of Way Permission",
    note: "Applicants are advised to keep all the required documents and drawings ready and accessible prior to initiating the application submission process.",
    frames: [
      "Certified copy of strip plan showing details of location of proposed utility, NH ROW, existing structure, other important highway assets. ",
      "Strip Plan/ Route Plan showing the OFC, Chainage, Cross Sections, Pipe Methods (Open/HDD), Width of ROW, Distance of proposed cable from the edge of ROW, important mile stone, intersections, cross drainage works etc.",
      "Crossing details: Plan & cross-section drawing showing size of pit, location of cable for (Open/HDD) method, depth of crossing (minimum of 2.0 m) from existing ground level with sealing at ends of pipe.",
      "Route index plan along the Highway showing following: a. Existing ROW of NH land. In yellow colour. b. Cable / pipe.in red colour.",
      "Table showing distance of cable/ pipe and ROW from centerline of Highway. (Minimum distance of cable / pipe is 2.0 m inside from edge of ROW edge).",
      "Any Other Supporting Drawings (Optional)",
    ],
    documents: [
      "Document of Power of Attorney in favour of authorized signatory.",
      "Copy of Department of Telecommunications (DOT) licence.",
      "Safety Clearance from Directorate of Electricity",
      "Safety Clearance from Oil Industry Safety Directorate",
      "Safety Clearance from state / central Pollution Control Board",
      "Any Other Statutory Clearance or Documents as applicable (Optional)",
      "Circle Rate Certificate",
      "Any document to indicate commercial activities are allowed on the land",
      "Document for special arrangements made by the applicant below the bed of the structure.",
      "Document to be attached showing calculation of license fee in accordance with Ministry’s Guideline No. NH/36094/01/2022/S&R(PB) dated 17 April, 2023.",

      "Utilized Proposed Area Picture",
      "Interception Drain Certificate/ Rain Water Drain Design",
      "Safety Clearance from Chief controller of Explosives ",
      "Safety Clearance from Petroleum and Explosives Safety Organization",
      "Any Other Statutory Clearance or Documents as applicable",
    ],
  };

  const handleCardClick = (id: number) => {
    setApplicationTypes((prevTypes) =>
      prevTypes.map((type) =>
        type.id === id
          ? { ...type, selected: true }
          : { ...type, selected: false },
      ),
    );
  };

  const handleNext = () => {
    const selectedType = applicationTypes.find((type) => type.selected);

    if (!selectedType) {
      console.warn("No application type selected");
      return;
    }

    if (selectedType.id === 1) {
      setOpenModal(true);
    } else if (selectedType.id === 2) {
      if (
        !profile?.organizations?.[0]?.gst_number ||
        profile?.user_category == 1
      ) {
        setOpenGSTAlert(true);
        // toast.error("Please enter GST number in profile before proceeding.");
        return;
      }
      setOpenROWModal(true);
    }

    dispatch(resetSiteRoadWaysState());
    dispatch(resetAmenityState());
    dispatch(resetSiteRoadwaysROW());
    dispatch(resetAmenityROWState());
    dispatch(resetApplicationDashboardState());
    dispatch(setActiveStep(0));
  };

  // Docs buttons inside cards
  const handleViewDocsClick = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (id === 1) setOpenRequiredDrawingModal(true); // Access -> Required Drawing
    if (id === 2) setOpenFrameModal(true); // ROW -> Frame
  };

  const handleROWModalClose = () => setOpenROWModal(false);
  const handleROWModalSubmit = () => {
    setOpenROWModal(false);
    dispatch(setApplicationType(2));
    dispatch(resetApplicationSubmitted());
    navigate("/stepper-row");
    dispatch(setCoordinateSite("start"));
  };

  const handleModalSubmit = () => {
    setOpenModal(false);
    const selectedType = applicationTypes.find((type) => type.selected);
    dispatch(setApplicationType(selectedType?.id));
    if (selectedType?.id === 1) {
      dispatch(resetApplicationSubmitted());
      navigate("/stepper");
    } else if (selectedType?.id === 2) {
      dispatch(resetApplicationSubmitted());
      navigate("/stepper-row");
      dispatch(setCoordinateSite("start"));
    }
  };

  const breadcrumbData = [{ label: "New Application", link: "#" }];
  const handleModalClose = () => setOpenModal(false);

  const closeRequiredDrawing = () => setOpenRequiredDrawingModal(false);
  const closeFrameDialog = () => setOpenFrameModal(false);

  return (
    <Box className="dashboard-container">
      <div className="page-content">
        {/* <DashboardHeader /> */}
        <GenericBreadCrums breadcrumbs={breadcrumbData} className="mt-6" />

        <Box className="dashboard-title-box">
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            New Application
          </Typography>
          <Typography
            variant="body1"
            className="dashboard-subtitle"
            align="center"
            sx={{ textAlign: "center" }}
          >
            Proceed with your application by selecting an option below. Choose
            between Access Permission for
            <br /> highway entry and exit approval or Right of Way(ROW) for
            utility installations.
          </Typography>
        </Box>

        <Grid
          container
          spacing={4}
          justifyContent="center"
          sx={{ mb: { xs: 7, sm: 0 } }}
        >
          {applicationTypes.map((type) => (
            <Grid item xs={12} md={4} key={type.id}>
              <Card
                className={`dashboard-card ${
                  type.selected ? "selected" : "not-selected"
                }`}
                onClick={() => handleCardClick(type.id)}
              >
                <CardContent className="dashboard-card-content">
                  {type.iconUrl}
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {type.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: 14, mb: 1.5 }}
                  >
                    {type.description}
                  </Typography>

                  <Button
                    variant="text"
                    fullWidth
                    endIcon={<KeyboardArrowRightIcon />}
                    onClick={(e) => handleViewDocsClick(type.id, e)}
                    sx={{
                      justifyContent: "space-between",
                      textTransform: "none",
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                      fontWeight: 600,
                      color: "#111827",
                      backgroundColor: "#F8F8F9",
                      boxShadow: "none",
                      "&:hover": { backgroundColor: "#F1F1F3" },
                      marginTop: type.id === 1 ? "20px" : undefined,
                    }}
                  >
                    {type.id === 1
                      ? "View Required Documents (21)"
                      : "View Required Documents (21)"}
                  </Button>
                </CardContent>

                <div className="radio-indicator">
                  <span
                    className={`radio-circle ${
                      type.selected ? "selected" : ""
                    }`}
                  >
                    {type.selected && (
                      <span className="check-mark">
                        <CheckIcon />
                      </span>
                    )}
                  </span>
                </div>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box className="dashboard-footer whitespace-nowrap">
          <Button
            variant="outlined"
            className="back-btn"
            onClick={() => navigate("/application-dashboard")}
          >
            Back to Dashboard
          </Button>

          <Button
            variant="contained"
            className="stepper-next-btn"
            onClick={handleNext}
            disabled={!applicationTypes.some((type) => type.selected)}
            sx={{
              "&.Mui-disabled": {
                backgroundColor: "#9E9E9E !important",
                color: "#fff",
              },
            }}
          >
            Proceed to Next
          </Button>
        </Box>

        {/* Required Drawing dialog (Access) */}
        <Dialog
          open={openRequiredDrawingModal}
          onClose={closeRequiredDrawing}
          fullWidth
          maxWidth="md"
          scroll="paper"
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
            <ArticleOutlinedIcon sx={{ color: "text.primary" }} />
            <Typography variant="h6" fontWeight={700} sx={{ ml: 1 }}>
              {accessRequired.title}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={closeRequiredDrawing}
              sx={{ ml: "auto" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography
              variant="body2"
              sx={{ mb: 2, fontSize: 12, color: "#3A1C73", fontWeight: 500 }}
            >
              {accessRequired.note}
            </Typography>

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Drawings List
            </Typography>
            <Box component="ol" sx={{ pl: 3, mb: 2 }}>
              {accessRequired.drawings.map((item, idx) => (
                <Typography
                  key={idx}
                  component="li"
                  variant="body2"
                  sx={{ mb: 1 }}
                >
                  {item}
                </Typography>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Documents List
            </Typography>
            <Box component="ol" sx={{ pl: 3 }}>
              {accessRequired.documents.map((item, idx) => (
                <Typography
                  key={idx}
                  component="li"
                  variant="body2"
                  sx={{ mb: 1 }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </DialogContent>
        </Dialog>

        {/* Frame dialog (ROW) */}
        <Dialog
          open={openFrameModal}
          onClose={closeFrameDialog}
          fullWidth
          maxWidth="md"
          scroll="paper"
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
            <ArticleOutlinedIcon sx={{ color: "text.primary" }} />
            <Typography variant="h6" fontWeight={700} sx={{ ml: 1 }}>
              {rowFrame.title}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={closeFrameDialog}
              sx={{ ml: "auto" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, fontSize: 12, color: "#3A1C73", fontWeight: 500 }}
            >
              {rowFrame.note}
            </Typography>

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Drawings List
            </Typography>
            <Box component="ol" sx={{ pl: 3, mb: 2 }}>
              {rowFrame.frames.map((item, idx) => (
                <Typography
                  key={idx}
                  component="li"
                  variant="body2"
                  sx={{ mb: 1 }}
                >
                  {item}
                </Typography>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Documents List
            </Typography>
            <Box component="ol" sx={{ pl: 3 }}>
              {rowFrame.documents.map((item, idx) => (
                <Typography
                  key={idx}
                  component="li"
                  variant="body2"
                  sx={{ mb: 1 }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </DialogContent>
        </Dialog>

        {/* Existing disclaimers */}
        <DisclaimerModal
          open={openModal}
          handleClose={handleModalClose}
          onSubmit={handleModalSubmit}
        />
        <DisclaimerROWModal
          open={openROWModal}
          handleClose={handleROWModalClose}
          onSubmit={handleROWModalSubmit}
        />
      </div>
      <AlertDialogBox
        open={openGSTAlert}
        onClose={() => setOpenGSTAlert(false)}
      />
    </Box>
  );
};

export default MainDashboard;
